import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ModelContext } from "../rdf/model";
import { loadDatasets, toSelections } from "../rdf/store";
import { DATASET_REGISTRY } from "../data/dataset-registry";
import type { DatasetDescriptor } from "../rdf/types";
import {
  deleteUserOntology,
  listUserOntologies,
  nextUserAccentColor,
  saveUserOntology,
  toDescriptor,
  validateTurtle,
  type StoredUserOntology,
} from "./user-datasets";

const VERSION_SELECTIONS_STORAGE_KEY = "oe-version-selections";

export interface UploadOutcome {
  fileName: string;
  ok: boolean;
  /** Parser error message when `ok` is false. */
  error?: string;
  datasetId?: string;
  label?: string;
  tripleCount?: number;
}

interface AppState {
  /** True until the very first store build completes. */
  isLoading: boolean;
  /** True while the store is being rebuilt after an upload/removal/version switch. */
  isRebuilding: boolean;
  loadError: string | null;
  /** The full RDF model context (every dataset at its selected version). Null until loading completes. */
  modelContext: ModelContext | null;
  /** Bundled + user datasets, in display order. */
  datasets: DatasetDescriptor[];
  activeDatasetIds: ReadonlySet<string>;
  setDatasetActive: (datasetId: string, active: boolean) => void;
  /** datasetId -> selected version id (datasets absent fall back to their newest version). */
  versionSelections: ReadonlyMap<string, string>;
  setDatasetVersion: (datasetId: string, versionId: string) => void;
  /** Parses, persists, and loads uploaded Turtle files. Returns one outcome per file. */
  uploadOntologies: (files: File[]) => Promise<UploadOutcome[]>;
  removeUserOntology: (datasetId: string) => Promise<void>;
}

const AppStateContext = createContext<AppState | null>(null);

function readStoredVersionSelections(): Map<string, string> {
  try {
    const raw = localStorage.getItem(VERSION_SELECTIONS_STORAGE_KEY);
    if (!raw) return new Map();
    return new Map(Object.entries(JSON.parse(raw) as Record<string, string>));
  } catch {
    return new Map();
  }
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [modelContext, setModelContext] = useState<ModelContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userOntologies, setUserOntologies] = useState<StoredUserOntology[] | null>(null);
  const [versionSelections, setVersionSelections] = useState<Map<string, string>>(
    readStoredVersionSelections,
  );
  const [activeDatasetIds, setActiveDatasetIds] = useState<Set<string>>(
    () =>
      new Set(
        DATASET_REGISTRY.filter((d) => d.enabledByDefault).map((d) => d.id),
      ),
  );
  // Monotonic build counter so a slow older rebuild can never clobber the
  // result of a newer one (uploads and version switches can overlap).
  const buildGeneration = useRef(0);

  // Load persisted user uploads once; the store build below waits for it.
  useEffect(() => {
    listUserOntologies()
      .then((stored) => {
        setUserOntologies(stored);
        setActiveDatasetIds((previous) => {
          const next = new Set(previous);
          for (const s of stored) next.add(s.id);
          return next;
        });
      })
      .catch(() => setUserOntologies([]));
  }, []);

  const datasets = useMemo<DatasetDescriptor[]>(
    () => [...DATASET_REGISTRY, ...(userOntologies ?? []).map(toDescriptor)],
    [userOntologies],
  );

  // (Re)build the shared store whenever the dataset list or version
  // selections change. All datasets are always parsed into one store — the
  // dataset switcher is a display-time filter (see state/filters.ts), not a
  // reload trigger.
  useEffect(() => {
    if (userOntologies === null) return; // wait for IndexedDB before first build
    const generation = ++buildGeneration.current;
    setIsRebuilding(true);

    loadDatasets(toSelections(datasets, versionSelections))
      .then(({ store, prefixes, datasetIdByGraphIri, versionIdByDatasetId }) => {
        if (generation !== buildGeneration.current) return;
        setModelContext({
          store,
          prefixes,
          datasetIdByGraphIri,
          versionIdByDatasetId,
          datasets,
        });
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (generation !== buildGeneration.current) return;
        const message = error instanceof Error ? error.message : String(error);
        setLoadError(`Failed to load ontology datasets: ${message}`);
      })
      .finally(() => {
        if (generation !== buildGeneration.current) return;
        setIsLoading(false);
        setIsRebuilding(false);
      });
  }, [datasets, versionSelections, userOntologies]);

  const setDatasetActive = useCallback((datasetId: string, active: boolean) => {
    setActiveDatasetIds((previous) => {
      const next = new Set(previous);
      if (active) next.add(datasetId);
      else next.delete(datasetId);
      return next;
    });
  }, []);

  const setDatasetVersion = useCallback((datasetId: string, versionId: string) => {
    setVersionSelections((previous) => {
      const next = new Map(previous);
      next.set(datasetId, versionId);
      try {
        localStorage.setItem(
          VERSION_SELECTIONS_STORAGE_KEY,
          JSON.stringify(Object.fromEntries(next)),
        );
      } catch {
        // Persistence is best-effort; the in-memory selection still applies.
      }
      return next;
    });
  }, []);

  const uploadOntologies = useCallback(
    async (files: File[]): Promise<UploadOutcome[]> => {
      const outcomes: UploadOutcome[] = [];
      const added: StoredUserOntology[] = [];
      let colorOffset = userOntologies?.length ?? 0;

      for (const file of files) {
        try {
          const text = await file.text();
          const validated = validateTurtle(text);
          const baseName = file.name.replace(/\.(ttl|turtle)$/i, "");
          const label = validated.selfLabel ?? baseName;
          const stored: StoredUserOntology = {
            id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            label,
            fileName: file.name,
            text,
            accentColor: nextUserAccentColor(colorOffset++),
            uploadedAt: new Date().toISOString(),
            namespaceIri: validated.namespaceIri,
            tripleCount: validated.tripleCount,
          };
          await saveUserOntology(stored);
          added.push(stored);
          outcomes.push({
            fileName: file.name,
            ok: true,
            datasetId: stored.id,
            label,
            tripleCount: validated.tripleCount,
          });
        } catch (error) {
          outcomes.push({
            fileName: file.name,
            ok: false,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      if (added.length > 0) {
        setUserOntologies((previous) => [...(previous ?? []), ...added]);
        setActiveDatasetIds((previous) => {
          const next = new Set(previous);
          for (const s of added) next.add(s.id);
          return next;
        });
      }
      return outcomes;
    },
    [userOntologies],
  );

  const removeUserOntology = useCallback(async (datasetId: string) => {
    await deleteUserOntology(datasetId);
    setUserOntologies((previous) =>
      (previous ?? []).filter((s) => s.id !== datasetId),
    );
    setActiveDatasetIds((previous) => {
      const next = new Set(previous);
      next.delete(datasetId);
      return next;
    });
  }, []);

  const value = useMemo<AppState>(
    () => ({
      isLoading,
      isRebuilding,
      loadError,
      modelContext,
      datasets,
      activeDatasetIds,
      setDatasetActive,
      versionSelections,
      setDatasetVersion,
      uploadOntologies,
      removeUserOntology,
    }),
    [
      isLoading,
      isRebuilding,
      loadError,
      modelContext,
      datasets,
      activeDatasetIds,
      setDatasetActive,
      versionSelections,
      setDatasetVersion,
      uploadOntologies,
      removeUserOntology,
    ],
  );

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState(): AppState {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
}
